import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import type { ActiveIncident } from "@/client/incident";
import styles from "./styles.module.css";
type Props = {
  incidents: ActiveIncident[];
};
const BAR_HEIGHT_CSS_VAR = "--incident-bar-height";
/** 最小化チップが上部中央で占める高さ。snackbar 等が被らないよう export する */
const CHIP_CLEARANCE_CSS_VAR = "--incident-chip-clearance";
/** 最小化チップの上端マージン(px)。styles.module.css の .chip top と一致させる */
const CHIP_TOP_MARGIN = 8;

/**
 * info severity の incident を画面上部の帯で表示する。
 * critical のような操作妨害はしないが、注意喚起として常時可視にする。
 *
 * 邪魔な場合は最小化ボタンで小さなピル表示に畳める。
 * 状態はセッション中のみ保持し、永続化しないので新規 incident は気付ける。
 *
 * バー / チップの top は CSS 変数 `--mode-banner-height` (監視・デバッグモード帯の合計高さ、
 * _app.tsx が設定) 分だけ下げ、上部のモード帯と被らないようにする。
 *
 * バーが展開中は実描画高さを CSS 変数 `--incident-bar-height` として `:root` に export し、
 * `position: fixed; top: 0` 系の要素 (AdminLayout の AppBar / AssessmentSearchHeader 等) が
 * 自分の top をその分オフセットすることで被りを避ける。最小化中は 0 を返す。
 *
 * 最小化中は代わりにチップの占有高さを `--incident-chip-clearance` として export し、
 * 上部中央に出る snackbar が被らないようにする (レイアウトは畳んだ分だけ詰める)。
 */
export const IncidentInfoBar: React.FC<Props> = t0 => {
  const $ = _c(11);
  const {
    incidents
  } = t0;
  const [minimized, setMinimized] = useState(false);
  const barRef = useRef(null);
  const chipRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== minimized) {
    t1 = () => {
      const root = document.documentElement;
      if (minimized) {
        root.style.setProperty(BAR_HEIGHT_CSS_VAR, "0px");
        const chip = chipRef.current;
        const clearance = chip ? chip.offsetHeight + CHIP_TOP_MARGIN : 0;
        root.style.setProperty(CHIP_CLEARANCE_CSS_VAR, `${clearance}px`);
        return () => {
          root.style.setProperty(CHIP_CLEARANCE_CSS_VAR, "0px");
        };
      }
      root.style.setProperty(CHIP_CLEARANCE_CSS_VAR, "0px");
      const el = barRef.current;
      if (!el) {
        return;
      }
      const update = () => {
        root.style.setProperty(BAR_HEIGHT_CSS_VAR, `${el.offsetHeight}px`);
      };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => {
        observer.disconnect();
        root.style.setProperty(BAR_HEIGHT_CSS_VAR, "0px");
      };
    };
    t2 = [minimized];
    $[0] = minimized;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useLayoutEffect(t1, t2);
  if (minimized) {
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Box component="button" type="button" ref={chipRef} className={styles.chip} aria-label={"\u969C\u5BB3\u60C5\u5831\u3092\u5C55\u958B\u3059\u308B"} onClick={() => setMinimized(false)}><Box className={styles.chipDot} aria-hidden={true} /><WarningAmberIcon className={styles.chipIcon} /><Typography component="span" className={styles.chipLabel}>障害情報あり</Typography></Box>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    return t3;
  }
  let t3;
  if ($[4] !== incidents) {
    t3 = incidents.map(_temp);
    $[4] = incidents;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <Box className={styles.list}>{t3}</Box>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Button className={styles.minimize} onClick={() => setMinimized(true)} startIcon={<CloseFullscreenIcon />} variant="outlined">最小化</Button>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4) {
    t6 = <Box ref={barRef} className={styles.root} role="status" aria-live="polite">{t4}{t5}</Box>;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
function _temp(incident) {
  return <Box key={incident.id} className={styles.item}><WarningAmberIcon className={styles.icon} /><Box className={styles.text}><Typography component="span" className={styles.title}>{incident.title}</Typography><Typography component="span" className={styles.body}>{incident.body}</Typography></Box></Box>;
}